import { useEffect, useMemo } from "react";
import { Grid, Box, Typography, Link } from "@mui/material";
import { Button } from "@covertree/ui";
import { Toast } from "@covertree/utils";
import { AGENT_HELP_ADDRESS, DISABLED_TOOLTIP_TEXT } from "@consts/index";
import { PolicyUsage } from "@covertree/agent-portal-graphql";
import {
  AdditionalInterestCard,
  sortAdditionalInterests,
} from "@covertree/features";
import {
  ActiveRenewalModal,
  AdditionalInterestModifiedModal,
  CancellationAlreadyIssuedModal,
  DefaultErrorModal,
  EndorsementInProgressModal,
  NonRenewalExistsModal,
  PolicyUpdatedSuccessfullyModal,
  RemoveAdditionalInterestConfirmationModal,
} from "@components/AdditionalInterestModals";

import { VALIDATION_ERROR_MESSAGE } from "./consts";
import type { PolicyDetailsContentProps } from "../../types";
import { useAdditionalInterests } from "./useAdditionalInterests";
import { useAdditionalInterestForm } from "./useAdditionalInterestForm";
import { AdditionalInterestFormModal } from "./AdditionalInterestFormModal";
import { AdditionalInterestsSkeleton } from "./AdditionalInterestsSkeleton";

export const LegacyAdditionalInterests = ({
  policy,
  loading,
  error,
}: PolicyDetailsContentProps) => {
  const isTenantPolicy = useMemo(
    () => policy?.mainUnitType === PolicyUsage.Tenant,
    [policy?.mainUnitType]
  );

  const {
    shouldDisableManage,
    mutationFeatureFlagEnabled,
    isValidationLoading,
    additionalInterests,
    validationReason,
    generalValidationError,
  } = useAdditionalInterests(policy);

  const {
    formMode,
    formSelectedInterest,
    interestsCountByType,
    isMutationLoading,
    mutationBlockReason,
    interestToRemove,
    removeModalOpen,
    successModalData,
    isSuccessModalOpen,
    handleAddClick,
    handleEditClick,
    handleReplaceClick,
    handleFormClose,
    handleFormSubmit,
    handleRemoveClick,
    handleRemoveClose,
    handleRemoveConfirm,
    handleCloseSuccessModal,
    handleBlockReasonModalClose,
  } = useAdditionalInterestForm({
    policy,
    validationReason,
  });

  useEffect(() => {
    if (generalValidationError) {
      Toast.showError(VALIDATION_ERROR_MESSAGE);
    }
  }, [generalValidationError]);

  if (!loading && (error || generalValidationError)) {
    return (
      <Box
        display="flex"
        flexDirection="column"
        alignItems="center"
        width={1}
        gap={2}>
        <Typography
          width={1}
          variant="subtitle1"
          color="black"
          textAlign="center">
          We are unable to load the Additional Interests list at the moment.
          Please try again later. If the issue persists, contact us via email{" "}
          <Link color="secondary" href={`mailto:${AGENT_HELP_ADDRESS}`}>
            {AGENT_HELP_ADDRESS}
          </Link>
          .
        </Typography>
      </Box>
    );
  }

  const addButton = mutationFeatureFlagEnabled ? (
    <Button
      variant="outlined"
      size="small"
      tooltipPlacement="top"
      disabled={shouldDisableManage}
      tooltip={shouldDisableManage ? DISABLED_TOOLTIP_TEXT : ""}
      onClick={handleAddClick}>
      Add additional interest
    </Button>
  ) : null;

  return (
    <>
      {!policy || loading || isValidationLoading ? (
        <AdditionalInterestsSkeleton />
      ) : additionalInterests.length === 0 ? (
        <Box
          display="flex"
          flexDirection="column"
          alignItems="center"
          gap={2}
          mt={8}>
          <Typography variant="subtitle1" color="text.disabled">
            Nothing here yet
          </Typography>
          {addButton}
        </Box>
      ) : (
        <>
          <Box display="flex" justifyContent="flex-end" mb={2}>
            {addButton}
          </Box>
          <Grid container spacing={2}>
            {sortAdditionalInterests(additionalInterests).map(
              (additionalInterest) => (
                <Grid
                  item
                  key={additionalInterest.additionalInterestId}
                  xs={12}>
                  <AdditionalInterestCard
                    fontSize={20}
                    disabled={shouldDisableManage}
                    tooltip={shouldDisableManage ? DISABLED_TOOLTIP_TEXT : ""}
                    tooltipPlacement={"top"}
                    additionalInterest={additionalInterest}
                    actionMenuItems={
                      mutationFeatureFlagEnabled
                        ? additionalInterest.isBilling
                          ? [
                              {
                                label: "Replace",
                                onClick: () =>
                                  handleReplaceClick(
                                    additionalInterest.additionalInterestId
                                  ),
                              },
                            ]
                          : [
                              {
                                label: "Edit",
                                onClick: () =>
                                  handleEditClick(
                                    additionalInterest.additionalInterestId
                                  ),
                              },
                              {
                                label: "Remove",
                                onClick: () =>
                                  handleRemoveClick(
                                    additionalInterest.additionalInterestId
                                  ),
                              },
                            ]
                        : []
                    }
                  />
                </Grid>
              )
            )}
          </Grid>
        </>
      )}

      <AdditionalInterestFormModal
        open={formMode !== null}
        onClose={handleFormClose}
        onSubmit={handleFormSubmit}
        mode={formMode ?? "add"}
        isSubmitting={isMutationLoading}
        isTenantPolicy={isTenantPolicy}
        selectedInterest={
          formMode === "edit" || formMode === "replace"
            ? formSelectedInterest
            : null
        }
        interestsCountByType={interestsCountByType}
      />

      {successModalData?.isRenewalActive ? (
        <AdditionalInterestModifiedModal
          isModalOpen={isSuccessModalOpen}
          onClose={handleCloseSuccessModal}
          isOfferSent={successModalData?.isOfferSent}
        />
      ) : (
        <PolicyUpdatedSuccessfullyModal
          isModalOpen={isSuccessModalOpen}
          onClose={handleCloseSuccessModal}
        />
      )}

      <RemoveAdditionalInterestConfirmationModal
        isModalOpen={removeModalOpen}
        additionalInterestName={interestToRemove?.name ?? ""}
        onClose={handleRemoveClose}
        onConfirm={handleRemoveConfirm}
        isSubmitting={isMutationLoading}
      />
      <DefaultErrorModal
        isModalOpen={
          mutationBlockReason === "NotKnownError" ||
          mutationBlockReason === "PolicyExpired"
        }
        onClose={handleBlockReasonModalClose}
      />
      <ActiveRenewalModal
        isModalOpen={mutationBlockReason === "ActiveRenewal"}
        onClose={handleBlockReasonModalClose}
      />
      <CancellationAlreadyIssuedModal
        isModalOpen={mutationBlockReason === "PolicyCancelled"}
        onClose={handleBlockReasonModalClose}
      />
      <EndorsementInProgressModal
        isModalOpen={mutationBlockReason === "ActiveEndorsement"}
        onClose={handleBlockReasonModalClose}
      />
      <NonRenewalExistsModal
        isModalOpen={mutationBlockReason === "NonRenewalIssued"}
        onClose={handleBlockReasonModalClose}
      />
    </>
  );
};
